import { useState, useEffect, useCallback, useRef } from 'react';
import type { Dispatch, SetStateAction, DependencyList } from 'react';
import api, { getList } from '../api/client';
import type { CallOptions, QueryParams } from '../api/client';
import type { ListResponse } from '../types/common';

// Loads a list endpoint with loading / error / refetch state.
// Replaces the bare `fetch(...).then(setX).catch(console.error)` in every module,
// which had no loading state and swallowed errors into the console.
//
//   const { data: events, loading, error, refetch } = useFetchList<ChurchEvent>('/api/events');
//   const { data: members } = useFetchList((p, o) => membersApi.list(p, o));

/**
 * Either a path to GET, or a function that fetches. The function form is what
 * modules use so they can compose several endpoints into one list.
 */
export type FetchSource<T> =
  | string
  | ((params?: QueryParams, options?: CallOptions) => Promise<ListResponse<T> | T[]>);

export interface UseFetchListOptions {
  params?: QueryParams;
  /** Extra values that should re-trigger the fetch when they change. */
  deps?: DependencyList;
  enabled?: boolean;
}

export interface UseFetchListResult<T> {
  data: T[];
  setData: Dispatch<SetStateAction<T[]>>;
  /** The unnormalised response — keep it when you need Page.totalElements. */
  raw: unknown;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export const useFetchList = <T = unknown>(
  source: FetchSource<T>,
  { params, deps = [], enabled = true }: UseFetchListOptions = {}
): UseFetchListResult<T> => {
  const [data, setData] = useState<T[]>([]);
  const [raw, setRaw] = useState<unknown>(null);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<Error | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const sourceRef = useRef(source);
  sourceRef.current = source;

  const load = useCallback(async () => {
    if (!enabled) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const fn = sourceRef.current;
      const payload =
        typeof fn === 'function'
          ? await fn(params, { signal: controller.signal })
          : await api.get<ListResponse<T>>(fn, { params, signal: controller.signal });

      if (controller.signal.aborted) return;
      setRaw(payload);
      setData(getList<T>(payload));
    } catch (err) {
      const e = err as Error;
      if (e?.name === 'AbortError' || controller.signal.aborted) return;
      setError(e);
      setData([]);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, JSON.stringify(params || null)]);

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, ...deps]);

  return { data, setData, raw, loading, error, refetch: load };
};

export default useFetchList;
